"use client";

import * as React from "react";
import { Info, Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import {
  isCourseSource,
  isExternalSource,
  isURLSource,
  StudyNotUploadedDocument,
  UploadableSource,
  UploadableURL,
} from "../../types";
import { cn } from "@/lib/utils";
import { CourseSourcesTable } from "./course-sources-table";
import { useNotebookContext } from "../../hooks/useNotebook";
import { CanvasCourse } from "@/lib/types";
import { useUserLimits } from "../../hooks/useUserLimits";
import { ThemedTabsList, ThemedTabsTrigger } from "./themed-tabs";
import { ExternalSourcesTab } from "./external-sources-tab";
import { AddLinkDialogContent } from "./add-link-dialog-content";
import { AddPasteTextDialogContent } from "./add-paste-text-dialog-content";
import { IconCanvasLogoSolid } from "@instructure/ui-icons";

interface AddSourcesModalProps {
  open: boolean;
  courses: CanvasCourse[];
  onClose: () => void;
  uploadSource?: (source: UploadableSource) => void;
  init: boolean;
}

type InvalidFileReason = "size" | "type";

type InvalidFile = {
  file: File;
  reason: InvalidFileReason;
  message: string;
};

export function AddSourcesModal({
  open,
  courses,
  onClose,
  init,
  uploadSource,
}: AddSourcesModalProps) {
  const { notebook, sources, courseFiles, loadingSources } =
    useNotebookContext();
  const { limits } = useUserLimits();

  const courseName = courses.find(
    (c) => c.id.toString() === notebook.course_id
  )?.default;

  const [selectedUploadableSources, setSelectedUploadableSources] =
    React.useState<UploadableSource[]>([]);
  const [invalidFiles, setInvalidFiles] = React.useState<InvalidFile[]>([]);
  const [modalState, setModalState] = React.useState<"main" | "link" | "text">(
    "main"
  );

  const [selectedTab, setSelectedTab] = React.useState<"own" | "course">(
    "course"
  );
  const [confirmedRights, setConfirmedRights] = React.useState(true);

  const allowedMimeTypes = React.useMemo(
    () => limits.supportedTypes.map((t) => t.mime),
    [limits]
  );

  const allowedFileExtensions = limits.supportedTypes.map((l) =>
    l.extensions.join(", ")
  );

  const maxFileSizeBytes = limits.maxFileSize;

  const selectedCourseSources = selectedUploadableSources.filter(
    isCourseSource
  ) as StudyNotUploadedDocument[];
  const selectedExternalSources = selectedUploadableSources.filter(
    isExternalSource
  ) as File[];
  const selectedLinkSources = selectedUploadableSources.filter(
    isURLSource
  ) as UploadableURL[];

  const handleConfirm = async () => {
    console.log(
      "uploading",
      selectedCourseSources,
      selectedExternalSources,
      selectedLinkSources
    );
    selectedCourseSources.forEach(uploadSource);
    selectedExternalSources.forEach(uploadSource);
    selectedLinkSources.forEach((link) => uploadSource(link));

    handleOpenChange();
  };

  function handleOpenChange() {
    setSelectedUploadableSources([]);
    setInvalidFiles([]);
    onClose();
    // Delay resetting modal state to prevent flash during close animation
    setTimeout(() => {
      setModalState("main");
    }, 300);
  }

  const confirmButtonIsDisabled =
    (selectedTab === "own" &&
      selectedExternalSources.length === 0 &&
      selectedLinkSources.length === 0) ||
    (selectedTab === "course" && selectedCourseSources.length === 0);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleOpenChange();
        }
      }}
    >
      <DialogContent className="bg-theme-0 overflow-y-scroll max-h-screen sm:max-w-[600px]">
        {modalState === "main" ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-left">
                <div className="text-sm text-theme-link mb-1">
                  Creating for {courseName}
                </div>
                <div className="text-2xl font-semibold">
                  Add sources to your study set
                </div>
                <div className="text-sm font-normal text-theme-1 mt-1">
                  Select from Canvas materials or add your own!
                </div>
              </DialogTitle>
            </DialogHeader>

            <Tabs value={selectedTab} className="w-full min-w-0">
              <ThemedTabsList className="mx-auto mb-2">
                <ThemedTabsTrigger
                  value="course"
                  onClick={() => setSelectedTab("course")}
                >
                  <IconCanvasLogoSolid />
                  Class files
                </ThemedTabsTrigger>
                <ThemedTabsTrigger
                  value="own"
                  onClick={() => setSelectedTab("own")}
                >
                  Add your own
                </ThemedTabsTrigger>
              </ThemedTabsList>

              <TabsContent value="course" className="w-full min-w-0">
                {loadingSources ? (
                  <div className="flex h-40 items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-theme-2" />
                  </div>
                ) : (
                  <CourseSourcesTable
                    allowedMimeTypes={allowedMimeTypes}
                    maxFileSizeBytes={maxFileSizeBytes}
                    sources={sources}
                    files={courseFiles}
                    selected={selectedCourseSources}
                    setSelected={setSelectedUploadableSources}
                  />
                )}
              </TabsContent>

              <TabsContent value="own" className="space-y-4 w-full">
                <ExternalSourcesTab
                  allowedMimeTypes={allowedMimeTypes}
                  allowedFileExtensions={allowedFileExtensions}
                  maxFileSizeBytes={maxFileSizeBytes}
                  selectedSources={selectedUploadableSources}
                  setSelectedSources={setSelectedUploadableSources}
                  invalidFiles={invalidFiles}
                  setInvalidFiles={setInvalidFiles}
                  onOpenLinkDialog={() => setModalState("link")}
                  onOpenPasteTextDialog={() => setModalState("text")}
                />
              </TabsContent>
            </Tabs>

            <div
              className={cn(
                "flex items-center gap-2 rounded-lg border px-3 py-2",
                "bg-amber-200/50 text-amber-900 border-amber-300",
                "text-xs leading-snug"
              )}
            >
              <Info className="h-3.5 w-3.5 flex-shrink-0" />
              <span>
                We'll use these files to generate study materials and provide
                cited answers.
              </span>
            </div>

            <div
              className={cn(
                "flex items-center gap-2 rounded-lg px-3",
                // "bg-amber-200/50 text-amber-900 border-amber-300",
                "text-xs leading-snug"
              )}
            >
              <input
                id="rights-confirmation"
                type="checkbox"
                checked={confirmedRights}
                onChange={(e) => setConfirmedRights(e.target.checked)}
                className="h-3 w-3 rounded border-amber-400 accent-amber-600"
              />
              <label
                htmlFor="rights-confirmation"
                className="cursor-pointer select-none text-theme-1"
              >
                I confirm I have the rights and permission to upload these
                files.
              </label>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={handleOpenChange}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={confirmButtonIsDisabled || !confirmedRights}
                className="bg-brand-gradient text-black hover:scale-110 transition"
              >
                Upload
              </Button>
            </div>
          </>
        ) : modalState === "link" ? (
          <AddLinkDialogContent
            onBack={() => setModalState("main")}
            onClose={handleOpenChange}
            selectedSources={selectedUploadableSources}
            setSelectedSources={setSelectedUploadableSources}
          />
        ) : (
          <AddPasteTextDialogContent
            onBack={() => setModalState("main")}
            onClose={handleOpenChange}
            onAddFile={(file) => {
              setSelectedUploadableSources((prev) => [...prev, file]);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
